import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuth } from '@/features/auth';
import { useSettingsFilters } from '../hooks';
import { expenseCategoriesApi, settingsQueryKeys } from '../api';
import { SettingsDataTable, SettingsAction, useSettingsColumns } from '../components';
import type { ExpenseCategory } from '../types';

export function ExpenseCategoriesPage() {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const { filters, updateFilter, setPage, setLimit, handleSort } = useSettingsFilters();

  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: settingsQueryKeys.expenseCategories.list(filters),
    queryFn: () => expenseCategoriesApi.getAll(filters),
  });

  const columns = useSettingsColumns<ExpenseCategory>({
    orderBy: filters.orderBy,
    order: filters.order,
    onSort: handleSort,
    resource: 'expense-category',
    apiEndpoint: 'expense-category',
    queryKey: 'expenseCategories',
    title: t('expenseCategory.title'),
  });

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('expenseCategory.pageTitle')}</h1>
        <div className="flex items-center gap-3">
          {hasAccess('expense', 'get') && (
            <Link to="/app/expenses">
              <Button variant="outline">{t('expense.pageTitle')}</Button>
            </Link>
          )}
          {hasAccess('expense-category', 'create') && (
            <SettingsAction
              title={t('expenseCategory.title')}
              apiEndpoint="expense-category"
              queryKey="expenseCategories"
              placeholder={t('expenseCategory.namePlaceholder') || t('enterName')}
              successMessageCreate={t('expenseCategory.createSuccess')}
              successMessageUpdate={t('expenseCategory.updateSuccess')}
            />
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <SettingsDataTable
          tableZoom={tableZoom}
          tableId="expense-categories"
          data={data?.results || []}
          columns={columns}
          filters={filters}
          updateFilter={updateFilter}
          isLoading={isLoading}
          isError={isError}
          error={error?.message}
          emptyMessage={t('expenseCategory.empty')}
          createButton={
            hasAccess('expense-category', 'create') ? (
              <SettingsAction
                title={t('expenseCategory.title')}
                apiEndpoint="expense-category"
                queryKey="expenseCategories"
              />
            ) : undefined
          }
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? 0}
          onPageChange={setPage}
          onLimitChange={setLimit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
